// Pure helpers behind the Skills Registry and the Agent Flow Map. Zero imports on purpose: this file
// is exercised by a plain `node:test` run (see registryView.test.ts) without the bundler tsconfig, so
// nothing here may reach for an alias import. The API types are re-declared structurally as the few
// fields these helpers read.

export type SkillMode = 'plan' | 'propose' | 'access' | 'rank' | 'report';

export interface FlowTrigger {
  type: 'check' | 'quota' | 'event';
  key: string;
  intervalDays?: number;
  count?: number;
  period?: string;
  event?: string;
}

/** The slice of a registry skill the flow map and the table helpers read. */
export interface FlowSkill {
  key: string;
  agentType: string;
  owner: string;
  mode: SkillMode;
  status: 'live' | 'dormant';
  produces: string | null;
  routes: string[];
  findingKinds: Record<string, readonly string[]>;
  triggers: FlowTrigger[];
  emitsCheckpoints: boolean;
  requires: string[];
  /** Declared tools registered as stubs (not wired yet). */
  stubTools?: string[];
}

export const SHARED_OWNER = 'shared';

/** Plain-language mode names — a viewer sees "Audit", never the manifest's `plan`. */
export const MODE_LABELS: Record<SkillMode, string> = {
  plan: 'Audit',
  rank: 'Planner',
  access: 'Access',
  propose: 'Proposal',
  report: 'Report',
};

/** The mode columns of the flow map, in pipeline order. */
export const MODE_ORDER: SkillMode[] = ['plan', 'rank', 'access', 'propose', 'report'];

export const STAGE_TITLES: Record<SkillMode, string> = {
  plan: 'Audits & reviews',
  rank: 'Planner',
  access: 'Access',
  propose: 'Proposals & drafts',
  report: 'Reports',
};

/** Readable fallback for a skill key when no curated label exists: `ad_launch_gate` → "Ad launch gate". */
export function humanizeSkillKey(key: string): string {
  const words = key.replace(/[-_]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/** One line per trigger, for the table and the map's trigger column. */
export function describeTrigger(t: FlowTrigger): string {
  switch (t.type) {
    case 'check':
      return t.intervalDays ? `${t.key} · every ${t.intervalDays}d` : `${t.key} · check`;
    case 'quota':
      return t.count && t.period ? `${t.key} · ${t.count}/${t.period}` : `${t.key} · quota`;
    case 'event':
      return t.event ?? t.key;
  }
}

// ─── Registry table ───────────────────────────────────────────────────────────

export const ALL_SETS = 'all';

/** Which "set" tab a skill belongs to: its agent, or Shared for the cross-role skills. */
export function skillSet(skill: Pick<FlowSkill, 'agentType' | 'owner'>): string {
  return skill.owner === SHARED_OWNER ? SHARED_OWNER : skill.agentType;
}

export function filterBySet<T extends Pick<FlowSkill, 'agentType' | 'owner'>>(skills: T[], set: string): T[] {
  return set === ALL_SETS ? skills : skills.filter((s) => skillSet(s) === set);
}

export interface RegistryStats {
  skills: number;
  sets: number;
  live: number;
  dormant: number;
}

export function registryStats(skills: Pick<FlowSkill, 'agentType' | 'owner' | 'status'>[]): RegistryStats {
  return {
    skills: skills.length,
    sets: new Set(skills.map(skillSet)).size,
    live: skills.filter((s) => s.status === 'live').length,
    dormant: skills.filter((s) => s.status === 'dormant').length,
  };
}

// ─── Flow map ─────────────────────────────────────────────────────────────────
// Generated from data, never drawn by hand: columns are the trigger kinds and the modes a role's
// skills actually use; edges come from the manifests — a trigger feeds the skills that declare it, an
// audit's finding sources feed the proposal skills that route them, and `produces` links an audit to
// its planner. Every role gets the same treatment, so a new module shows up here with no UI work.

export interface FlowNode {
  id: string;
  label: string;
  column: string;
  dormant: boolean;
  /** The skill can hold a run on the always-ask floor (offers emit_checkpoint). */
  checkpoints: boolean;
  /** Tools the skill declares that are not wired yet: it runs, but records these as gaps. */
  waitingOn: string[];
  /** A trigger node rather than a skill. */
  trigger: boolean;
}

export interface FlowColumn {
  id: string;
  title: string;
  /** Visual tone; the component maps this to a CSS module class. */
  tone: 'trigger' | SkillMode;
  nodes: FlowNode[];
}

export interface FlowEdge {
  from: string;
  to: string;
  /** Why the edge exists, for the tooltip. */
  reason: string;
}

export interface FlowGraph {
  columns: FlowColumn[];
  edges: FlowEdge[];
  /** Skill keys with nothing downstream: they surface straight into the shared inbox. */
  terminal: string[];
}

export const TRIGGER_COLUMN = 'triggers';

export function buildFlowGraph(
  skills: FlowSkill[],
  labelFor: (key: string) => string = humanizeSkillKey,
): FlowGraph {
  const byKey = new Map(skills.map((s) => [s.key, s]));
  const edges: FlowEdge[] = [];
  const seenEdge = new Set<string>();
  const addEdge = (from: string, to: string, reason: string) => {
    const id = `${from}→${to}`;
    if (seenEdge.has(id) || from === to) return;
    seenEdge.add(id);
    edges.push({ from, to, reason });
  };

  // Trigger nodes: one per distinct description, feeding every skill that declares it.
  const triggerNodes = new Map<string, FlowNode>();
  for (const s of skills) {
    for (const t of s.triggers) {
      const label = describeTrigger(t);
      const id = `trigger:${label}`;
      if (!triggerNodes.has(id)) {
        triggerNodes.set(id, { id, label, column: TRIGGER_COLUMN, dormant: false, checkpoints: false, waitingOn: [], trigger: true });
      }
      addEdge(id, s.key, `${t.type} trigger`);
    }
  }

  // Finding routing: an audit writes sources (findingKinds keys); proposal skills route them.
  for (const audit of skills) {
    const sources = Object.keys(audit.findingKinds);
    if (sources.length === 0) continue;
    for (const target of skills) {
      if (target.key === audit.key) continue;
      const shared = target.routes.filter((r) => sources.includes(r));
      if (shared.length > 0) addEdge(audit.key, target.key, `routes ${shared.join(', ')} findings`);
    }
  }

  // An audit that names the planner its tasks go to.
  for (const s of skills) {
    if (s.produces && byKey.has(s.produces)) addEdge(s.key, s.produces, 'produces tasks for');
  }

  const columns: FlowColumn[] = [];
  if (triggerNodes.size > 0) {
    columns.push({
      id: TRIGGER_COLUMN,
      title: 'Triggers',
      tone: 'trigger',
      nodes: [...triggerNodes.values()].sort((a, b) => a.label.localeCompare(b.label)),
    });
  }
  for (const mode of MODE_ORDER) {
    const nodes = skills
      .filter((s) => s.mode === mode)
      .sort((a, b) => Number(a.status === 'dormant') - Number(b.status === 'dormant') || a.key.localeCompare(b.key))
      .map<FlowNode>((s) => ({
        id: s.key,
        label: labelFor(s.key),
        column: mode,
        dormant: s.status === 'dormant',
        checkpoints: s.emitsCheckpoints,
        waitingOn: [...(s.stubTools ?? [])],
        trigger: false,
      }));
    if (nodes.length > 0) columns.push({ id: mode, title: STAGE_TITLES[mode], tone: mode, nodes });
  }

  const hasOut = new Set(edges.map((e) => e.from));
  const terminal = skills.filter((s) => !hasOut.has(s.key)).map((s) => s.key);
  return { columns, edges, terminal };
}
